import React, { useCallback, useEffect, useState } from 'react';
import { useAbility } from '@casl/react';
import { Popover } from '@gafe/common-ui';
import merge from 'classnames';
import { filter } from 'lodash';

import { ButtonGroup } from '@/components/generic/ButtonGroup';
import { IconCart } from '@/components/icons/IconCart';
import { IconFullInventory } from '@/components/icons/IconFullInventory';
import { IconInformation } from '@/components/icons/IconInformation';
import { IconSomeInventory } from '@/components/icons/IconSomeInventory';
import {
  getOrderItemName,
  getOrderItemPrice,
  isEquipment,
  isInventory,
  isProductType,
} from '@/components/modals/SubmitOrder/constants';
import { useOrderContext, useOrderDispatch } from '@/components/modals/SubmitOrder/helpers';
import { AbilityContext } from '@/hooks/ability/Can';
import { useDeviceDetection } from '@/hooks/useDeviceDetection';
import { MediaType, OrderInventoryType, ProductType, ProposalType } from '@/types';
import { formatPriceWithCurrency } from '@/utils/format-price';

import { ProductDescription } from '../../OrderDetailsStep/components/ProductDescription';
import { OrderBlock } from '../OrderBlock';

import { Props } from './types';

import styles from './InventoryType.module.scss';

export const getInventoryButtons = (isMobileView: boolean) => [
  {
    icon: <IconFullInventory />,
    label: isMobileView ? 'Full' : 'Full inventory',
    value: OrderInventoryType['Full Inventory'],
  },
  {
    icon: <IconSomeInventory />,
    label: isMobileView ? 'Some' : 'Some inventory',
    value: OrderInventoryType['Some Inventory'],
  },
  {
    icon: <IconCart />,
    label: isMobileView ? 'Need' : 'No inventory',
    value: OrderInventoryType['Need Inventory'],
  },
];

export const InventoryType = ({ proposal }: Props) => {
  const state = useOrderContext();
  const dispatch = useOrderDispatch();
  const mediaType = useDeviceDetection();
  const isMobileView = mediaType === MediaType.Mobile;
  const [orderInventoryType, setOrderInventoryType] = useState<OrderInventoryType | null>(null);
  const products = filter(state.visibleOrderItems, oi => isEquipment(oi) && isInventory(oi));
  const totalPrice = products.reduce(
    (sum, item) => sum + getOrderItemPrice(state, item, proposal),
    0,
  );
  const shouldShowOrderSection = products.length > 0;
  const shouldShowInventoryProducts =
    orderInventoryType === OrderInventoryType['Full Inventory'] ||
    orderInventoryType === OrderInventoryType['Some Inventory'];
  const ability = useAbility(AbilityContext);
  const isBulkFulfillmentEnabled =
    ability.can('view', 'Bulk Fulfillment Program') &&
    proposal.proposalType === ProposalType['Timberline Solar'];
  const productsPV = filter(
    state.visibleOrderItems,
    oi => isEquipment(oi) && isProductType(oi, ProductType.PV),
  );
  const designedQuantity = productsPV.reduce(
    (acc, product) => acc + (product?.orderItem?.quantityByDesign ?? 0),
    0,
  );

  useEffect(() => {
    if (state.order.inventoryType) {
      setOrderInventoryType(state.order.inventoryType);
    }
  }, [state.order.inventoryType]);

  const handleOrderInventoryType = useCallback(
    (inventoryType: OrderInventoryType) => {
      setOrderInventoryType(inventoryType);
      dispatch({
        type: 'UPDATE_ORDER',
        payload: {
          inventoryType,
        },
      });
    },
    [dispatch],
  );

  if (!isBulkFulfillmentEnabled || !products.length) {
    return null;
  }

  return (
    <>
      <div className={styles.order_inventory_type_select}>
        <OrderBlock hideValue title="What is your solar shingle inventory level?">
          <div className={styles.designed_quantity}>
            <span>
              <b>Designed Quantity:</b>
            </span>
            <span>{designedQuantity} modules</span>

            <Popover text="Quantity of modules you need for this design." title="Designed Quantity">
              <IconInformation color="#0054CC" size="13" />
            </Popover>
          </div>
          <div className={styles.button_group}>
            <ButtonGroup
              active={orderInventoryType}
              buttons={getInventoryButtons(isMobileView)}
              classes={{ button: styles.button }}
              onSelect={handleOrderInventoryType}
            />
          </div>

          {shouldShowOrderSection && (
            <div
              className={merge(styles.inventory_products_wrapper, {
                [styles.inventory_products_wrapper_show]: shouldShowInventoryProducts,
              })}
            >
              <br />
              <div className={styles.order_section_title}>
                <span>Complete Your Order</span>
                {!state.isPriceHidden && <span>{formatPriceWithCurrency(totalPrice)}</span>}
              </div>
              {products.map(item => (
                <React.Fragment key={item.orderItem.productId}>
                  <br />
                  <OrderBlock
                    classes={{ title: styles.product_title }}
                    color="#FAFAFA"
                    divider
                    hideValue={state.isPriceHidden}
                    key={item.priceBookEntry.productId}
                    smallHeader
                    title="Project Completion Kit"
                    value={getOrderItemPrice(state, item, proposal)}
                  >
                    <ProductDescription
                      classes={{ title: styles.title, description: styles.description }}
                      description={getOrderItemName(item)}
                      title="WHAT'S INSIDE"
                    />
                  </OrderBlock>
                </React.Fragment>
              ))}
            </div>
          )}
        </OrderBlock>
      </div>
      <br />
    </>
  );
};
